import { useNostr } from '@nostrify/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from './useCurrentUser';
import { useNostrPublish } from './useNostrPublish';
import { useToast } from './useToast';

/**
 * Fetches and manages the current user's block list.
 * Uses NIP-51 follow sets (kind 30000) with d="blocked" as a private block list.
 * Blocked users are stored as `p` tags.
 */
export function useBlockList() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { mutateAsync: publish } = useNostrPublish();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['nostr', 'block-list', user?.pubkey ?? ''],
    queryFn: async ({ signal }) => {
      if (!user) return [];
      const [event] = await nostr.query(
        [{ kinds: [30000], authors: [user.pubkey], '#d': ['blocked'], limit: 1 }],
        { signal: AbortSignal.any([signal, AbortSignal.timeout(5000)]) },
      );
      return event?.tags.filter(([name]) => name === 'p').map(([, pubkey]) => pubkey) ?? [];
    },
    enabled: !!user,
    staleTime: 60 * 1000,
  });

  const blockedPubkeys: string[] = query.data ?? [];

  function isBlocked(pubkey: string) {
    return blockedPubkeys.includes(pubkey);
  }

  async function block(pubkey: string) {
    if (!user) {
      toast({ title: 'Not logged in', description: 'You must be logged in to block someone.', variant: 'destructive' });
      return;
    }
    if (isBlocked(pubkey)) return;

    const [existing] = await nostr.query(
      [{ kinds: [30000], authors: [user.pubkey], '#d': ['blocked'], limit: 1 }],
      { signal: AbortSignal.timeout(5000) },
    );
    const existingTags = existing?.tags ?? [['d', 'blocked']];

    // Ensure d tag is present
    const hasDTag = existingTags.some(([name]) => name === 'd');
    const baseTags = hasDTag ? existingTags : [['d', 'blocked'], ...existingTags];

    try {
      await publish({
        kind: 30000,
        content: '',
        tags: [...baseTags, ['p', pubkey]],
      });
      await queryClient.invalidateQueries({ queryKey: ['nostr', 'block-list', user.pubkey] });
      toast({ title: 'Blocked', description: 'This user has been blocked.' });
    } catch (e) {
      console.error(e);
      toast({ title: 'Error', description: 'Failed to block user. Please try again.', variant: 'destructive' });
    }
  }

  async function unblock(pubkey: string) {
    if (!user) {
      toast({ title: 'Not logged in', description: 'You must be logged in to unblock someone.', variant: 'destructive' });
      return;
    }
    if (!isBlocked(pubkey)) return;

    const [existing] = await nostr.query(
      [{ kinds: [30000], authors: [user.pubkey], '#d': ['blocked'], limit: 1 }],
      { signal: AbortSignal.timeout(5000) },
    );
    const existingTags = existing?.tags ?? [['d', 'blocked']];
    const filteredTags = existingTags.filter(([name, value]) => !(name === 'p' && value === pubkey));

    try {
      await publish({
        kind: 30000,
        content: '',
        tags: filteredTags,
      });
      await queryClient.invalidateQueries({ queryKey: ['nostr', 'block-list', user.pubkey] });
      toast({ title: 'Unblocked', description: 'This user has been unblocked.' });
    } catch (e) {
      console.error(e);
      toast({ title: 'Error', description: 'Failed to unblock user. Please try again.', variant: 'destructive' });
    }
  }

  return {
    blockedPubkeys,
    isBlocked,
    block,
    unblock,
    isLoading: query.isLoading,
  };
}
